/**
 * Collapsible section header shared by the edit-property, edit-apartment, and
 * edit-room wizards.
 *
 * A description longer than MAX_INLINE_DESCRIPTION_CHARS is replaced by an (i)
 * sitting next to the title, in place of the copy that would otherwise print
 * underneath it. Shorter ones still print as before.
 */
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { InfoTooltip } from "@/components/admin/v2/primitives/InfoTooltip";

/**
 * Descriptions of this many characters or fewer (spaces counted) print under
 * the title; anything longer is replaced by the (i). Measuring characters
 * rather than sentences keeps the rule identical in every language — counting
 * sentences broke on German, which spaces its abbreviations ("z. B.") so a
 * single sentence read as three.
 */
const MAX_INLINE_DESCRIPTION_CHARS = 140;

export function Section({
  id,
  title,
  description,
  defaultOpen = false,
  alwaysShowDescription = false,
  children,
}: {
  /** Anchor for deep links (focusSection). */
  id?: string;
  title: string;
  description?: string;
  /** Revenue section starts open when deep-linked via focusSection. */
  defaultOpen?: boolean;
  /**
   * Keep the description on the page however long it is, for copy the reader
   * must not miss — text spelling out what a destructive action cascades to,
   * where putting it behind a hover would be a safety regression rather than
   * tidying.
   */
  alwaysShowDescription?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isLongDescription =
    !!description &&
    !alwaysShowDescription &&
    description.length > MAX_INLINE_DESCRIPTION_CHARS;

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section
        id={id}
        className="rounded-lg border bg-muted/30 px-4 py-4 space-y-3 scroll-mt-4"
      >
        <div className="flex w-full items-center gap-1.5">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              /* flex-1 so the button's own box fills the row up to the info
                 button/chevron — otherwise the strip of empty space beside a
                 short title has no click target and only the title text
                 itself toggles the section. */
              className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
            >
              <span className="min-w-0 space-y-0.5">
                <span
                  role="heading"
                  aria-level={3}
                  className="block text-sm font-semibold"
                >
                  {title}
                </span>
                {description && !isLongDescription && (
                  <span className="block text-xs text-muted-foreground">
                    {description}
                  </span>
                )}
              </span>
            </button>
          </CollapsibleTrigger>
          {isLongDescription && <InfoTooltip description={description} />}
          {/* Decoration, not a control: a second button here would announce the
              same name as the trigger, and marking it aria-hidden would leave a
              pointer user focused on something screen readers cannot see. */}
          <ChevronDown
            aria-hidden="true"
            className={`ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
        <CollapsibleContent className="space-y-3">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}
